import { Check } from 'lucide-react';
import { Track } from '../../../school_panel/components/ui';
import { RAG_STEPS, RAG_STEP_COUNT, RAG_SECTION_LABEL } from './steps';

interface Props {
  step: number;
  /** Called with a step number at or before the current one; later steps are never offered. */
  onGoToStep: (n: number) => void;
  /** Embedding has finished: show 100% instead of (step - 1) / 6. */
  complete?: boolean;
  /** A step is processing: the step list must not navigate away. */
  busy?: boolean;
  className?: string;
}

/** Left-hand progress card: percentage, track and the step list. Only completed and current steps are clickable. */
export default function RagSetupProgress({ step, onGoToStep, complete = false, busy = false, className = '' }: Props) {
  const pct = complete ? 100 : Math.round(((step - 1) / RAG_STEP_COUNT) * 100);
  return (
    <section aria-label="Setup progress" className={`min-w-0 self-start rounded-[20px] border border-line bg-surface p-[22px] shadow-card ${className}`}>
      <p className={`mb-1.5 ${RAG_SECTION_LABEL}`}>Progress</p>
      <p className="mb-2 flex items-baseline gap-2">
        <span className="font-mono text-[28px] font-semibold text-ink">{pct}%</span>
        {' '}
        <span className="text-[13px] text-muted">step {step} of {RAG_STEP_COUNT}</span>
      </p>
      <Track value={pct} tone="slate" label="Setup progress" className="mb-[22px]" />
      <ol className="flex flex-col">
        {RAG_STEPS.map((s, i) => {
          const n = i + 1;
          const done = n < step;
          const cur = n === step;
          const last = n === RAG_STEP_COUNT;
          return (
            <li key={s.label} className={`relative flex gap-3 ${last ? '' : 'pb-5'}`}>
              {!last && <span aria-hidden className={`absolute bottom-0 left-[17px] top-9 w-0.5 ${done ? 'bg-slate' : 'bg-line'}`} />}
              <button type="button" aria-label={`Go to step ${n}: ${s.label}`} aria-current={cur ? 'step' : undefined}
                disabled={n > step || busy} onClick={() => onGoToStep(n)}
                className={`relative grid h-9 w-9 flex-none place-items-center rounded-full border-2 font-mono text-[13px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-default ${
                  done ? 'border-slate bg-slate text-white hover:brightness-110'
                    : cur ? 'border-accent bg-accent text-navy ring-[5px] ring-accent/20'
                      : 'border-line-strong bg-surface text-muted'}`}>
                {done ? <Check size={16} aria-hidden /> : n}
              </button>
              <span className="min-w-0 pt-[7px]">
                <span className={`block text-sm font-semibold ${n > step ? 'text-muted' : 'text-ink'}`}>{s.label}</span>
                <span className="block text-xs text-muted">{s.hint}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
